import {
  FGTextInput,
  FGTextAreaInput,
  FGSelectInput,
  FGCheckboxInput,
} from "nsitools-react";
import * as React from "react";
import { useHistory } from "react-router";
import * as Yup from "yup";

import { ERoutes } from "../../../../ERoutes";
import { FGMultiTranslate, SmallFormGenerator } from "../../../../components";
import { useGlobalData } from "../../../../contexts";
import { useApiService, useCrudApi, useTl } from "../../../../hooks";
import { ETLCodes } from "../../../../locales";
import {
  CharacteristicValuesApi,
  CharacteristicValuesDto,
  CharacteristicValuesTranslationDto,
  ELab,
} from "../../../../api";
import { useReferential } from "../../../../hooks/useReferential";

export interface ICharacteristicValuesDetailPageProps {
  characteristicValueId: number;
  editMode: boolean;
}

export const CharacteristicValuesDetailPage: React.FunctionComponent<ICharacteristicValuesDetailPageProps> = ({
  characteristicValueId,
  editMode,
}) => {
  const { t } = useTl();
  const history = useHistory();
  const api = useApiService(CharacteristicValuesApi);
  const { languages } = useGlobalData();
  const [labs, lLoading] = useReferential((a) => a.getLabs(), false, []);
  const { data, loading, saveItem, saving, deleteItem, deleting } = useCrudApi(
    {
      getApiFn: (id = characteristicValueId) =>
        id > 0
          ? api.get(id)
          : new CharacteristicValuesDto({
              lab: ELab.ZOOLYX,
              characteristicValuesTranslations: [
                ...languages.map(
                  (l) =>
                    new CharacteristicValuesTranslationDto({
                      languageId: l.languageId,
                    })
                ),
              ],
            }).toJSON(),
      saveApiFn: (d: CharacteristicValuesDto) => api.save(d),
      onSavedRoute: (d) =>
        `${ERoutes.characteristicValues}/${d.healthValueId}/detail/edit`,
      deleteApiFn: (d) => api.delete(d.healthValueId),
      onDeletedRoute: (d) => `${ERoutes.characteristicValues}`,
    },
    [characteristicValueId]
  );

  const FormSchema = React.useMemo(() => {
    return Yup.object().shape({
      lab: Yup.string().nullable().required(t(ETLCodes.Required)),
      value: Yup.string().nullable().required(t(ETLCodes.Required)),
      characteristicValuesTranslations: Yup.array().of(
        Yup.object().shape({
          text: Yup.string().nullable().required(t(ETLCodes.Required)),
        })
      ),
    });
  }, [t]);

  return (
    data && (
      <SmallFormGenerator
        initialValues={data}
        onSubmit={saveItem}
        editMode={editMode}
        loading={loading}
        onCancel={() => history.push(ERoutes.characteristicValues)}
        saving={saving}
        deleting={deleting}
        showDeleteButton={characteristicValueId > 0}
        onDelete={deleteItem}
        validationSchema={FormSchema}
      >
        <FGSelectInput
          name="lab"
          label={t(ETLCodes.Laboratory)}
          items={labs}
          loading={lLoading}
        />
        <FGTextInput name="value" label={t(ETLCodes.LaboMapping)} />
        <FGCheckboxInput
          name="okForInheritance"
          label={t(ETLCodes.OkForInheritance)}
        />
        <FGMultiTranslate
          label={ETLCodes.Name}
          translationsArrayField="characteristicValuesTranslations"
          translationField="text"
          translationDto={CharacteristicValuesTranslationDto}
        />
        <FGTextAreaInput label={t(ETLCodes.Remark)} name="remark" />
      </SmallFormGenerator>
    )
  );
};
